import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Fab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import ResponsiveIframe from "components/ResponsiveIframe";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { EmailThemeRequest } from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";

const styles = {
  titleColumn: { width: "200px", fontWeight: "bold" },
};

const EmailTemplateDetails = () => {
  const navigate = useNavigate();
  const { formatMessage: t } = useIntl();
  const {
    emailTemplateEditUrl,
    emailTemplatesUrl,
    notificationService,
    emailService,
  } = useGlobalContext();

  const [deleteDialogOpen, deleteDialogOpenActions] = useBoolean(false);
  const { registerPromise } = useTrashablePromises();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();

  const { data, isLoading, isError } = useQuery(["getEmailTemplate", id], () =>
    emailService.getEmailTemplate(id!)
  );

  const emailTemplate = data?.data;
  useTitle(emailTemplate?.name || "Email Template Details");

  const themeQuery = useQuery(
    ["getEmailThemeByOrganization", emailTemplate?.organizationId],
    () =>
      notificationService.getEmailThemeByOrganization(
        EmailThemeRequest.fromJS({
          organizationId: emailTemplate?.organizationId,
        })
      ),
    {
      enabled: !!emailTemplate?.organizationId,
    }
  );

  if (isLoading || themeQuery.isLoading) return <Loader />;
  if (isError || themeQuery.isError) {
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="fetchError" />
        </Alert>
      </Box>
    );
  }

  const handleDelete = async () => {
    try {
      const response = await registerPromise(
        emailService.deleteEmailTemplate(emailTemplate?.id!)
      );
      queryClient.invalidateQueries("emailTemplates");
      queryClient.invalidateQueries("getEmailTemplate");

      deleteDialogOpenActions.setFalse();
      navigate(emailTemplatesUrl());

      return response;
    } catch (error) {
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  const rows = [
    {
      title: t({ id: "organization" }),
      component: <span>{emailTemplate?.organizationName}</span>,
    },
    {
      title: t({ id: "templateName" }),
      component: <span>{emailTemplate?.name}</span>,
    },
    {
      title: t({ id: "emailTheme" }),
      component: (
        <span>{themeQuery?.data?.data?.inheritedOrganizationName}</span>
      ),
    },
    {
      title: t({ id: "subject" }),
      component: <span>{emailTemplate?.subject}</span>,
    },
    {
      title: t({ id: "senderName" }),
      component: <span>{emailTemplate?.senderName}</span>,
    },
    {
      title: t({ id: "senderEmail" }),
      component: <span>{emailTemplate?.senderEmail}</span>,
    },
    {
      title: t({ id: "ccEmails" }),
      component: <span>{emailTemplate?.ccEmails}</span>,
    },
    {
      title: t({ id: "bccEmails" }),
      component: <span>{emailTemplate?.bccEmails}</span>,
    },
  ].filter((r) => !!r.component);

  return (
    <>
      <RedirectBackButton
        redirectRoute={emailTemplatesUrl()}
        labelKey="backToListButtonLabel"
      />
      <h2>{emailTemplate?.name}</h2>

      <Box my={3} display="flex" justifyContent="center">
        <Link to={emailTemplateEditUrl(emailTemplate?.id)}>
          <Fab color="primary">
            <EditIcon />
          </Fab>
        </Link>
        <Box ml={4} />
        <Fab color="secondary" onClick={deleteDialogOpenActions.setTrue}>
          <DeleteIcon />
        </Fab>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            {rows.map(({ title, component }) => (
              <TableRow key={title}>
                <TableCell sx={styles.titleColumn}>{title}</TableCell>
                <TableCell>{component}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <h2>{t({ id: "previewLabel" })}</h2>

      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>
                <ResponsiveIframe
                  srcDoc={emailTemplate?.body!}
                  title="emailTemplatePreview"
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <DeleteEntityDialog
        dialogTitleKey="areYouSure"
        rawContent={t(
          {
            id: "deleteEmailTemplateConfirmation",
          },
          { emailTemplateName: emailTemplate?.name }
        )}
        onCancel={deleteDialogOpenActions.setFalse}
        handleDelete={handleDelete}
        open={deleteDialogOpen}
      />
    </>
  );
};

export default EmailTemplateDetails;
